<template>
  <bottom-sheet
    v-if="visible"
    :visible="visible"
    :show-close-icon="false"
    :min-height="''"
    @close="$emit('close')"
  >
    <div class="-mt-2 flex w-full max-w-2xl justify-center px-2 pb-4 lg:px-5">
      <div class="relative flex w-full flex-col items-start rounded bg-white">
        <img
          class="absolute right-0 top-3 cursor-pointer"
          src="@/assets/close-icon-gray.svg"
          alt="close icon"
          @click="$emit('close')"
        />

        <div class="flex w-full flex-col gap-4">
          <div class="text-left">
            <div class="mb-2 flex items-center gap-1">
              <img
                v-if="isAddView || isEditView"
                class="top-4 h-4 w-4 cursor-pointer"
                src="@/assets/back-icon-gray.svg"
                alt=""
                @click="handleBack"
              />
              <h3 class="text-3xl text-dark-blue">
                {{ title }}
              </h3>
            </div>
            <p class="text-xs font-normal text-neutral-subtle lg:text-base">
              {{ description }}
            </p>
          </div>

          <div
            v-if="isSettingsView"
            class="flex flex-col gap-2 overflow-y-auto px-2 pt-2"
            style="max-height: 300px; overscroll-behavior: contain"
          >
            <div
              v-for="email in emails"
              :key="email.id"
              class="flex w-full items-start justify-between"
            >
              <div
                class="mb-5 flex max-w-[calc(100%-48px)] flex-col items-start gap-1"
              >
                <p
                  class="break-all text-left text-base font-medium text-body-color"
                >
                  {{ email.value }}
                </p>
                <p
                  v-if="email.rate_confirmation_delivery_alias?.length > 0"
                  class="mb-1 overflow-auto pr-2 text-left text-xs font-normal text-body-color"
                >
                  <span class="font-medium">Recipient Code:</span>
                  {{ email.rate_confirmation_delivery_alias }}
                </p>
                <p
                  v-if="
                    email.rate_confirmation_delivery_alias_description?.length >
                    0
                  "
                  class="mb-1 overflow-auto pr-2 text-left text-xs font-normal text-body-color"
                >
                  <span class="font-medium">Description:</span>
                  {{ email.rate_confirmation_delivery_alias_description }}
                </p>
                <p class="text-xs font-normal text-body-color">
                  Created on {{ formatCreatedAt(email.created_at) }}
                </p>
              </div>

              <div class="flex items-start gap-3">
                <svg
                  class="h-5 min-h-[20px] w-5 min-w-[20px] shrink-0 cursor-pointer"
                  style="margin-top: 1px"
                  viewBox="0 0 24 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  @click="editUser(email)"
                >
                  <path
                    d="M13 4.39062H6C5.44772 4.39062 5 4.83834 5 5.39062V19.3906C5 19.9429 5.44772 20.3906 6 20.3906H18C18.5523 20.3906 19 19.9429 19 19.3906V12.3906"
                    stroke="#6C7C9D"
                    stroke-width="2"
                  />
                  <path
                    d="M18.5971 3.53523C18.9876 3.14471 19.6208 3.14471 20.0113 3.53523L20.9212 4.44511C21.3117 4.83564 21.3117 5.4688 20.9212 5.85932L14.0195 12.761C13.8756 12.9049 13.6913 13.0015 13.4911 13.0378L12.3793 13.2398C11.6921 13.3646 11.0919 12.7644 11.2167 12.0772L11.4186 10.9654C11.4549 10.7652 11.5515 10.5808 11.6954 10.4369L18.5971 3.53523Z"
                    fill="#6C7C9D"
                  />
                </svg>

                <img
                  class="-mt-0.5 w-6 cursor-pointer"
                  src="@/assets/delete-icon.svg"
                  alt="trash icon"
                  @click="handleRemoveEmailView(email)"
                />
              </div>
            </div>
          </div>

          <div
            v-else-if="isAddView || isEditView"
            class="flex flex-col gap-3 text-left"
          >
            <div class="w-full">
              <dropdown-select
                class="w-full"
                :options="VerifiedEmails"
                :default-selected="isEditView ? form.email : ''"
                :disabled="isEditView"
                :default="isEditView ? form.email : 'Select Email Address'"
                :full-width="true"
                label="verified email"
                @input="handleSelect"
                @scroll-bottom="loadMoreUsers"
              />
            </div>
            <span
              class="flex w-full items-center gap-1 text-left text-2xs font-bold uppercase text-dark-blue"
            >
              Recipient Code (max 5 characters)
              <info-tooltip
                color-scheme="dark"
                content=""
                class="relative"
                style="top: 2px"
                size="xs"
                content-width-class="w-64"
              >
                <div class="text-left font-normal normal-case">
                  Add a 5-character code to send rate cons only to users linked
                  to this group or team, to avoid sending every rate con to
                  everyone in the company
                </div>
              </info-tooltip>
            </span>

            <form-field
              label=""
              :value="form.alias"
              placeholder="Enter Recipient Code"
              name="alias"
              :on-input="onInput"
              :error="form.errors.alias"
              :remove-y-margin="true"
              :maxlength="5"
            />
            <div>
              <label
                class="w-full text-left text-2xs font-bold uppercase text-dark-blue"
              >
                Description (max 200 characters)
              </label>
              <textarea
                v-model="form.aliasDescription"
                placeholder="Enter description"
                maxlength="200"
                class="input-padding mb-4 h-24 w-full resize-none rounded border border-transparent bg-off-white text-1md font-normal hover:border-placeholder-gray focus:bg-white focus:text-dark-gray focus:outline-none focus:ring-2 focus:ring-black"
                name="aliasDescription"
                @input="onInput"
              />
              <p
                v-if="form.errors.aliasDescription"
                class="text-xs text-red-500"
              >
                {{ form.errors.aliasDescription }}
              </p>
            </div>
          </div>

          <div
            class="ml-0 mt-2 flex h-12 gap-4 lg:ml-auto [&>*]:flex-1 md:[&>*]:flex-initial"
            :class="{
              'mt-20 lg:mt-2': isRemoveView,
            }"
          >
            <ButtonComponent
              v-if="!isSettingsView"
              @click="$emit('close')"
            >
              Cancel
            </ButtonComponent>
            <ButtonComponent
              v-if="isRemoveView"
              variant="primary"
              @click="handleRemoveEmail"
            >
              Yes, remove
            </ButtonComponent>
            <ButtonComponent
              v-else
              :class="[isEditView ? 'lg:w-[150px]' : 'lg:w-[130px]']"
              variant="primary"
              :disabled="isButtonDisabled"
              @click="handleEmailView"
            >
              {{
                isEditView
                  ? 'Save Changes'
                  : isSettingsView
                    ? 'Add Email'
                    : 'Save'
              }}
            </ButtonComponent>
          </div>
        </div>
      </div>
    </div>
  </bottom-sheet>
</template>

<script setup>
import {
  reactive,
  ref,
  computed,
  onMounted,
  defineProps,
  defineEmits,
} from 'vue';
import dayjs from 'dayjs';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import {
  ButtonComponent,
  FormField,
  BottomSheet,
  DropdownSelect,
  InfoTooltip,
} from '@/components';
import { isEmail } from '@/common/validators';

defineProps({
  visible: Boolean,
  emails: Array,
});

const emit = defineEmits(['close']);

const store = useStore();
const toast = useToast();

const VerifiedEmails = ref([]);
const page = ref(1);
const totalPages = ref(1);

onMounted(async () => {
  await fetchUsers();
});

const view = ref('settings');
const isSettingsView = computed(() => view.value === 'settings');
const isAddView = computed(() => view.value === 'add-email');
const isRemoveView = computed(() => view.value === 'remove-email');

const removedEmail = ref('');
const editingEmailId = ref(null);

const form = reactive({
  email: '',
  confirmEmail: '',
  alias: '',
  aliasDescription: '',
  errors: {
    email: '',
    confirmEmail: '',
    alias: '',
    aliasDescription: '',
  },
});

const title = computed(() => {
  if (view.value === 'settings') return 'Rate Con Settings';
  if (view.value === 'remove-email') return 'Remove Email Address';
  if (view.value === 'edit-email') return 'Edit Email Address';
  return 'Select Verified Email';
});

const description = computed(() => {
  if (view.value === 'settings')
    return `Add one or more email addresses to be notified of new rate confirmations sent to your company. Rate confirmations emailed to you by brokers using Highway will only notify the email address(es) provided below.`;
  if (view.value === 'remove-email')
    return 'Are you sure you want to remove this email?';
  if (view.value === 'edit-email')
    return 'You can only update the alias and description for this email.';
  return 'Choose an email address to receive rate confirmation notifications for your company.';
});

const formatCreatedAt = (date) => dayjs(date).format('M/D/YYYY [at] h:mm a');

const onInput = (e) => {
  const { name, value } = e.target;
  if (name === 'email' || name === 'confirmEmail') {
    form[name] = value;
    form.errors[name] = isEmail(value) ? '' : 'Must be a valid email.';
  } else if (name === 'alias') {
    form.alias = value.slice(0, 5);
    form.errors.alias = '';
  } else if (name === 'aliasDescription') {
    form.aliasDescription = value.slice(0, 200);
    form.errors.aliasDescription = '';
  }
};

const fetchUsers = async () => {
  if (page.value > totalPages.value) return;
  const res = await store.dispatch('users/getCompanyUsers', {
    companyId: store.state.users.currentUser.company.id,
    verifiedUsers: true,
    page: page.value,
    perPage: 50,
  });

  if (Array.isArray(res?.data) && res.data.length) {
    VerifiedEmails.value.push(
      ...res.data.map((user) => user.email).filter(Boolean)
    );

    totalPages.value = parseInt(res.headers.totalpages);
  }
};

const handleSelect = (option) => {
  console.log(option);
  form.email = option;
  form.confirmEmail = option;
  form.errors.email = '';
  form.errors.confirmEmail = '';
};

const loadMoreUsers = () => {
  page.value++;
  fetchUsers();
};

const handleBack = () => {
  view.value = 'settings';
};

const handleRemoveEmailView = (email) => {
  removedEmail.value = email;
  view.value = 'remove-email';
};

const handleRemoveEmail = async () => {
  try {
    const res = await store.dispatch('emails/inactivateEmail', {
      id: removedEmail.value.id,
    });

    if ([200, 201, 204].includes(res?.status)) {
      getRateConfirmationEmails();
      toast?.open({
        type: 'info',
        message: 'Email successfully removed',
        position: 'top-right',
        duration: 5000,
      });
    }
  } catch (error) {
    console.log('remove error: ', error);
    toast?.open({
      type: 'error',
      message: 'An error occurred. Please try again.',
      position: 'top-right',
      duration: 5000,
    });
  } finally {
    emit('close');
  }
};

const getRateConfirmationEmails = async () => {
  const query = `?q[is_type_eq]=rate_confirmation&q[carrier_id_eq]=${store.state.users.currentUser.company.carrier_id}`;
  await store.dispatch('emails/getEmails', { query });
};

const resetForm = () => {
  form.email = '';
  form.confirmEmail = '';
  form.alias = '';
  form.aliasDescription = '';
  form.errors.email = '';
  form.errors.confirmEmail = '';
  form.errors.alias = '';
  form.errors.aliasDescription = '';
  editingEmailId.value = null;
};

const handleEmailView = async () => {
  if (isSettingsView.value) {
    resetForm();
    view.value = 'add-email';
    return;
  }

  if (form.alias && !form.aliasDescription.trim()) {
    form.errors.aliasDescription =
      'Description is required if alias is present.';
    return;
  }

  if (isEditView.value) {
    await handleUpdateEmail();
    return;
  }

  const packetId =
    store.state.onboardingPacket?.onboarding_id ||
    store.state.emails.emails?.[0]?.email_addressable_id ||
    store.state.users.verifiedUsers?.[0]?.id;

  try {
    const res = await store.dispatch('emails/createEmail', {
      carrierId: store.state.users.currentUser.company.carrier_id,
      isType: 'rate_confirmation',
      value: form.email,
      onboardingPackeId: packetId,
      alias: form.alias || null,
      description: form.aliasDescription || null,
      addressableType: 'Carrier',
      addressableId: store.state.users.currentUser.company.carrier_id,
    });

    if ([200, 201, 204].includes(res?.status)) {
      getRateConfirmationEmails();
      toast?.open({
        type: 'info',
        message: 'Email Saved',
        position: 'top-right',
        duration: 5000,
      });
      emit('close');
    }
  } catch (error) {
    if (error?.status === 422 && error?.response?.data) {
      const messages = Object.entries(error.response.data).map(
        ([field, errors]) => {
          const label = field.charAt(0).toUpperCase() + field.slice(1);
          return errors.map((msg) => `${label} ${msg}.`).join(' ');
        }
      );

      toast?.open({
        type: 'error',
        message: messages.join(' '),
        position: 'top-right',
        duration: 5000,
      });
      return;
    }

    toast?.open({
      type: 'error',
      message: 'An error occurred. Please try again.',
      position: 'top-right',
      duration: 5000,
    });
  }
};

const handleUpdateEmail = async () => {
  try {
    const res = await store.dispatch('emails/updateEmail', {
      id: editingEmailId.value,
      carrierId: store.state.users.currentUser.company.carrier_id,
      value: form.email,
      isType: 'rate_confirmation',
      alias: form.alias || null,
      description: form.aliasDescription || null,
    });

    if ([200, 201, 204].includes(res?.status)) {
      getRateConfirmationEmails();
      toast?.open({
        type: 'info',
        message: 'Email Updated',
        position: 'top-right',
        duration: 5000,
      });
    }
  } catch (error) {
    console.error('update error: ', error);
    toast?.open({
      type: 'error',
      message: 'An error occurred. Please try again.',
      position: 'top-right',
      duration: 5000,
    });
  } finally {
    emit('close');
  }
};

const isButtonDisabled = computed(() => {
  if (isSettingsView.value) return false;

  const hasValidEmails =
    form.email &&
    form.confirmEmail &&
    isEmail(form.email) &&
    isEmail(form.confirmEmail) &&
    form.email === form.confirmEmail &&
    !form.errors.email &&
    !form.errors.confirmEmail;

  const aliasAndDescValid =
    !form.alias || (form.alias && form.aliasDescription.trim().length > 0);

  return !(hasValidEmails && aliasAndDescValid);
});
const isEditView = computed(() => view.value === 'edit-email');

const editUser = (emailObj) => {
  console.log('emailObj: ', emailObj);
  editingEmailId.value = emailObj.id;
  form.email = emailObj.value;
  form.confirmEmail = emailObj.value;
  form.alias = emailObj.rate_confirmation_delivery_alias || '';
  form.aliasDescription =
    emailObj.rate_confirmation_delivery_alias_description || '';
  view.value = 'edit-email';
};
</script>

<style scoped>
input:not([type='submit']):focus,
textarea:focus {
  /* @apply focus:outline-input; */
  @apply border border-body-color outline-none;
}

input[type='submit']:focus {
  @apply outline-none;
}
</style>
